import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, options, placeholder, className, id, ...rest },
  ref
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label
          htmlFor={selectId}
          className="text-[11px] font-[family-name:var(--font-label)] uppercase tracking-[0.12em] text-[color:var(--color-text-secondary)]"
        >
          {label}
        </label>
      )}
      <div
        className={cn(
          'relative rounded-md border bg-white/[0.03] transition-colors',
          error ? 'border-[color:var(--color-error)]/60' : 'border-white/10 focus-within:border-[color:var(--color-brand-magenta)]/70'
        )}
      >
        <select
          ref={ref}
          id={selectId}
          aria-invalid={!!error}
          className={cn(
            'w-full appearance-none bg-transparent px-4 py-3 pr-10 text-[15px] text-white outline-none',
            className
          )}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-[color:var(--color-bg-mid)]">
              {opt.label}
            </option>
          ))}
        </select>
        <svg
          aria-hidden
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[color:var(--color-text-muted)]"
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {hint && !error && (
        <p className="text-xs text-[color:var(--color-text-muted)]">{hint}</p>
      )}
      {error && <p className="text-xs text-[color:var(--color-error)]" role="alert">{error}</p>}
    </div>
  );
});
